import React, { useMemo, useState } from "react";
import { Info, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect, { US_STATES } from "@/components/jea/JeaSelect";
import DropUpload from "@/components/jea/DropUpload";
import MoneyInput from "@/components/jea/MoneyInput";
import { COURT_LEVELS, EMPLOYMENT_STATUSES, NATURE_OF_DEBT } from "@/lib/pricing";

const SubSectionHeader = ({ children }) => (
  <div className="mb-4">
    <div className="text-accent-pink font-semibold text-[13px] tracking-[0.08em] uppercase">
      {children}
    </div>
    <div className="mt-2 h-px bg-accent-pink/20" />
  </div>
);

const Row = ({ children, cols = 2 }) => (
  <div
    className="grid gap-3"
    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
  >
    {children}
  </div>
);

const AddressFields = ({ value = {}, onChange, testPrefix }) => (
  <div className="space-y-3">
    <JeaInput
      placeholder="Street"
      value={value.street || ""}
      onChange={(e) => onChange({ ...value, street: e.target.value })}
      data-testid={`${testPrefix}-street`}
    />
    <div className="grid grid-cols-3 gap-3">
      <JeaInput
        placeholder="City"
        value={value.city || ""}
        onChange={(e) => onChange({ ...value, city: e.target.value })}
        data-testid={`${testPrefix}-city`}
      />
      <JeaSelect
        value={value.state || ""}
        onChange={(e) => onChange({ ...value, state: e.target.value })}
        placeholder="State"
        data-testid={`${testPrefix}-state`}
      >
        {US_STATES.map(([code, name]) => (
          <option key={code} value={code}>
            {code} — {name}
          </option>
        ))}
      </JeaSelect>
      <JeaInput
        placeholder="ZIP"
        value={value.zip || ""}
        onChange={(e) => onChange({ ...value, zip: e.target.value.replace(/[^\d-]/g, "").slice(0, 10) })}
        data-testid={`${testPrefix}-zip`}
      />
    </div>
  </div>
);

// ---------- Sub-steps ----------
const BasicInfo = ({ debtor, patch }) => (
  <div>
    <SubSectionHeader>Basic Info</SubSectionHeader>
    <div className="space-y-3">
      <JeaInput
        placeholder="Debtor Full Name (required)"
        value={debtor.full_name || ""}
        onChange={(e) => patch({ full_name: e.target.value })}
        data-testid="debtor-full-name"
      />
      <JeaInput
        placeholder="Aliases / DBA (optional, comma-separated)"
        value={(debtor.aliases || []).join(", ")}
        onChange={(e) =>
          patch({ aliases: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })
        }
        data-testid="debtor-aliases"
      />
      <Row>
        <JeaInput
          type="date"
          placeholder="Date of Birth (optional)"
          value={debtor.dob || ""}
          onChange={(e) => patch({ dob: e.target.value })}
          data-testid="debtor-dob"
        />
        <JeaInput
          placeholder="Last 4 of SSN (optional)"
          value={debtor.ssn_last4 || ""}
          onChange={(e) => patch({ ssn_last4: e.target.value.replace(/\D/g, "").slice(0, 4) })}
          data-testid="debtor-ssn"
        />
      </Row>
    </div>
  </div>
);

const AddressHistory = ({ debtor, patch }) => (
  <div>
    <SubSectionHeader>Address History</SubSectionHeader>
    <div className="text-[12px] font-semibold text-text-secondary mb-2 uppercase tracking-[0.06em]">
      Current Address
    </div>
    <AddressFields
      value={debtor.current_address || {}}
      onChange={(v) => patch({ current_address: v })}
      testPrefix="debtor-current-address"
    />

    <div className="mt-6 space-y-4">
      {(debtor.prior_addresses || []).map((a, i) => (
        <div key={i} className="space-y-2" data-testid={`prior-address-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">
              Prior Address {i + 1}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = [...debtor.prior_addresses];
                next.splice(i, 1);
                patch({ prior_addresses: next });
              }}
              className="text-text-secondary hover:text-danger p-1"
              data-testid={`prior-address-remove-${i}`}
            >
              <X size={14} />
            </button>
          </div>
          <AddressFields
            value={a}
            onChange={(v) => {
              const next = [...(debtor.prior_addresses || [])];
              next[i] = v;
              patch({ prior_addresses: next });
            }}
            testPrefix={`prior-address-${i}`}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() => patch({ prior_addresses: [...(debtor.prior_addresses || []), {}] })}
        className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
        data-testid="add-prior-address"
      >
        <Plus size={14} /> Add Another Address
      </button>
    </div>
  </div>
);

const Employment = ({ debtor, patch }) => (
  <div>
    <SubSectionHeader>Employment</SubSectionHeader>
    <div className="space-y-3">
      <JeaInput
        placeholder="Current Employer Name"
        value={debtor.current_employer?.name || ""}
        onChange={(e) => patch({ current_employer: { ...(debtor.current_employer || {}), name: e.target.value } })}
        data-testid="employer-name"
      />
      <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">
        Employer Address
      </div>
      <AddressFields
        value={debtor.current_employer?.address || {}}
        onChange={(v) => patch({ current_employer: { ...(debtor.current_employer || {}), address: v } })}
        testPrefix="employer-address"
      />
      <JeaSelect
        value={debtor.employment_status || "unknown"}
        onChange={(e) => patch({ employment_status: e.target.value })}
        data-testid="employment-status"
      >
        {EMPLOYMENT_STATUSES.map((s) => (
          <option key={s.value} value={s.value}>
            {s.label}
          </option>
        ))}
      </JeaSelect>
    </div>

    <div className="mt-6 space-y-4">
      {(debtor.prior_employers || []).map((emp, i) => (
        <div key={i} className="space-y-2" data-testid={`prior-employer-${i}`}>
          <div className="flex items-center justify-between">
            <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">
              Prior Employer {i + 1}
            </div>
            <button
              type="button"
              onClick={() => {
                const next = [...debtor.prior_employers];
                next.splice(i, 1);
                patch({ prior_employers: next });
              }}
              className="text-text-secondary hover:text-danger p-1"
            >
              <X size={14} />
            </button>
          </div>
          <JeaInput
            placeholder="Employer Name"
            value={emp.name || ""}
            onChange={(e) => {
              const next = [...(debtor.prior_employers || [])];
              next[i] = { ...next[i], name: e.target.value };
              patch({ prior_employers: next });
            }}
          />
          <AddressFields
            value={emp.address || {}}
            onChange={(v) => {
              const next = [...(debtor.prior_employers || [])];
              next[i] = { ...next[i], address: v };
              patch({ prior_employers: next });
            }}
            testPrefix={`prior-employer-${i}-address`}
          />
        </div>
      ))}
      <button
        type="button"
        onClick={() =>
          patch({ prior_employers: [...(debtor.prior_employers || []), { name: "", address: {} }] })
        }
        className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
        data-testid="add-prior-employer"
      >
        <Plus size={14} /> Add Another Employer
      </button>
    </div>
  </div>
);

const Assets = ({ debtor, patch }) => {
  const a = debtor.assets || {};
  return (
    <div>
      <SubSectionHeader>Assets (Optional)</SubSectionHeader>

      {/* Real Property */}
      <div className="space-y-3">
        <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">
          Real Property
        </div>
        {(a.real_property || []).map((rp, i) => (
          <div key={i} className="grid grid-cols-[1fr_180px_36px] gap-2 items-center">
            <JeaInput
              placeholder="Address"
              value={rp.address || ""}
              onChange={(e) => {
                const next = [...a.real_property];
                next[i] = { ...next[i], address: e.target.value };
                patch({ assets: { ...a, real_property: next } });
              }}
            />
            <MoneyInput
              placeholder="$0.00"
              valueCents={rp.estimated_value_cents || 0}
              onChange={(cents) => {
                const next = [...a.real_property];
                next[i] = { ...next[i], estimated_value_cents: cents };
                patch({ assets: { ...a, real_property: next } });
              }}
              testid={`real-property-value-${i}`}
            />
            <button
              type="button"
              onClick={() => {
                const next = [...a.real_property];
                next.splice(i, 1);
                patch({ assets: { ...a, real_property: next } });
              }}
              className="p-2 rounded text-text-secondary hover:text-danger"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            patch({
              assets: {
                ...a,
                real_property: [...(a.real_property || []), { address: "", estimated_value_cents: 0 }],
              },
            })
          }
          className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="add-real-property"
        >
          <Plus size={14} /> Add Property
        </button>
      </div>

      {/* Vehicles */}
      <div className="mt-6 space-y-3">
        <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">Vehicles</div>
        {(a.vehicles || []).map((v, i) => (
          <div key={i} className="grid grid-cols-[80px_1fr_1fr_1fr_36px] gap-2 items-center">
            <JeaInput
              placeholder="Year"
              value={v.year || ""}
              onChange={(e) => {
                const next = [...a.vehicles];
                next[i] = { ...next[i], year: e.target.value.replace(/\D/g, "").slice(0, 4) };
                patch({ assets: { ...a, vehicles: next } });
              }}
            />
            <JeaInput
              placeholder="Make"
              value={v.make || ""}
              onChange={(e) => {
                const next = [...a.vehicles];
                next[i] = { ...next[i], make: e.target.value };
                patch({ assets: { ...a, vehicles: next } });
              }}
            />
            <JeaInput
              placeholder="Model"
              value={v.model || ""}
              onChange={(e) => {
                const next = [...a.vehicles];
                next[i] = { ...next[i], model: e.target.value };
                patch({ assets: { ...a, vehicles: next } });
              }}
            />
            <JeaInput
              placeholder="VIN (optional)"
              value={v.vin || ""}
              onChange={(e) => {
                const next = [...a.vehicles];
                next[i] = { ...next[i], vin: e.target.value };
                patch({ assets: { ...a, vehicles: next } });
              }}
            />
            <button
              type="button"
              onClick={() => {
                const next = [...a.vehicles];
                next.splice(i, 1);
                patch({ assets: { ...a, vehicles: next } });
              }}
              className="p-2 rounded text-text-secondary hover:text-danger"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            patch({
              assets: {
                ...a,
                vehicles: [...(a.vehicles || []), { year: "", make: "", model: "", vin: "" }],
              },
            })
          }
          className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="add-vehicle"
        >
          <Plus size={14} /> Add Vehicle
        </button>
      </div>

      {/* Bank Accounts */}
      <div className="mt-6 space-y-3">
        <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em]">
          Bank Accounts (Known)
        </div>
        {(a.bank_accounts || []).map((b, i) => (
          <div key={i} className="grid grid-cols-[1fr_160px_36px] gap-2 items-center">
            <JeaInput
              placeholder="Institution"
              value={b.institution || ""}
              onChange={(e) => {
                const next = [...a.bank_accounts];
                next[i] = { ...next[i], institution: e.target.value };
                patch({ assets: { ...a, bank_accounts: next } });
              }}
            />
            <JeaInput
              placeholder="Last 4 (optional)"
              value={b.last4 || ""}
              onChange={(e) => {
                const next = [...a.bank_accounts];
                next[i] = { ...next[i], last4: e.target.value.replace(/\D/g, "").slice(0, 4) };
                patch({ assets: { ...a, bank_accounts: next } });
              }}
            />
            <button
              type="button"
              onClick={() => {
                const next = [...a.bank_accounts];
                next.splice(i, 1);
                patch({ assets: { ...a, bank_accounts: next } });
              }}
              className="p-2 rounded text-text-secondary hover:text-danger"
            >
              <X size={14} />
            </button>
          </div>
        ))}
        <button
          type="button"
          onClick={() =>
            patch({ assets: { ...a, bank_accounts: [...(a.bank_accounts || []), { institution: "", last4: "" }] } })
          }
          className="text-accent-pink hover:text-accent-pink-light text-[13px] font-semibold inline-flex items-center gap-1"
          data-testid="add-bank-account"
        >
          <Plus size={14} /> Add Bank Account
        </button>
      </div>

      {/* Other Assets */}
      <div className="mt-6">
        <div className="text-[12px] font-semibold text-text-secondary uppercase tracking-[0.06em] mb-2">
          Other Assets
        </div>
        <textarea
          value={a.other || ""}
          onChange={(e) => patch({ assets: { ...a, other: e.target.value } })}
          placeholder="e.g. boat, business equity, collectibles"
          className="w-full min-h-[80px] rounded-[8px] bg-bg-tertiary border border-border-default focus:border-accent-pink focus:shadow-pink-glow p-3 text-[14px] text-white placeholder:text-text-muted focus:outline-none transition-all"
          data-testid="other-assets"
        />
      </div>
    </div>
  );
};

const JudgmentDetails = ({ debtor, patch }) => {
  const j = debtor.judgment || {};
  return (
    <div>
      <SubSectionHeader>Judgment Details</SubSectionHeader>
      <div className="space-y-3">
        <JeaInput
          placeholder="Case Number"
          value={j.case_number || ""}
          onChange={(e) => patch({ judgment: { ...j, case_number: e.target.value } })}
          className="jea-mono"
          data-testid="judgment-case-number"
        />
        <JeaInput
          placeholder="Court Name"
          value={j.court_name || ""}
          onChange={(e) => patch({ judgment: { ...j, court_name: e.target.value } })}
          data-testid="judgment-court-name"
        />
        <Row>
          <JeaSelect
            value={j.court_level || ""}
            onChange={(e) => patch({ judgment: { ...j, court_level: e.target.value } })}
            placeholder="Court Level"
            data-testid="judgment-court-level"
          >
            {COURT_LEVELS.map((l) => (
              <option key={l} value={l}>{l}</option>
            ))}
          </JeaSelect>
          <JeaSelect
            value={j.state || ""}
            onChange={(e) => patch({ judgment: { ...j, state: e.target.value } })}
            placeholder="State"
            data-testid="judgment-state"
          >
            {US_STATES.map(([code, name]) => (
              <option key={code} value={code}>
                {code} — {name}
              </option>
            ))}
          </JeaSelect>
        </Row>
        <Row>
          <JeaInput
            type="date"
            value={j.date_entered || ""}
            onChange={(e) => patch({ judgment: { ...j, date_entered: e.target.value } })}
            data-testid="judgment-date-entered"
          />
          <MoneyInput
            placeholder="$0.00"
            valueCents={j.amount_cents || 0}
            onChange={(cents) => patch({ judgment: { ...j, amount_cents: cents } })}
            testid="judgment-amount"
          />
        </Row>
        <Row>
          <div>
            <label
              className="jea-caption flex items-center gap-1.5 mb-1.5"
              htmlFor="judgment-interest-rate"
            >
              Interest Rate (%)
              <span
                className="inline-flex items-center justify-center w-4 h-4 rounded-full text-[10px] text-text-secondary hover:text-accent-pink cursor-help"
                title="Post-judgment interest rate per your state's statute. Used to auto-compute accrued interest on the Debtor Profile and Reports. Common values: 4–12% depending on jurisdiction."
                data-testid="interest-rate-info"
              >
                <Info size={12} />
              </span>
            </label>
            <JeaInput
              id="judgment-interest-rate"
              placeholder="e.g. 5.25"
              value={j.interest_rate === 0 || j.interest_rate === undefined ? "" : String(j.interest_rate)}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^\d.]/g, "");
                const parts = raw.split(".");
                const clean = parts.length > 1
                  ? `${parts[0]}.${parts[1].slice(0, 2)}`
                  : parts[0];
                const num = parseFloat(clean);
                if (clean === "") {
                  patch({ judgment: { ...j, interest_rate: 0 } });
                } else if (Number.isFinite(num) && num >= 0 && num < 100) {
                  patch({ judgment: { ...j, interest_rate: num } });
                }
              }}
              type="text"
              inputMode="decimal"
              data-testid="judgment-interest-rate"
            />
          </div>
          <div>
            <label className="jea-caption block mb-1.5">Nature of Underlying Debt</label>
            <JeaSelect
              value={j.nature_of_debt || ""}
              onChange={(v) => patch({ judgment: { ...j, nature_of_debt: v } })}
              placeholder="Nature of Underlying Debt"
              data-testid="judgment-nature"
            >
              {NATURE_OF_DEBT.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </JeaSelect>
          </div>
        </Row>
      </div>

      <div className="mt-5 flex gap-3 rounded-[8px] bg-bg-tertiary border-l-2 border-accent-pink p-4">
        <Info className="text-accent-pink shrink-0 mt-0.5" size={16} />
        <p className="text-[14px] text-text-secondary leading-5">
          The nature of the underlying debt affects whether the judgment survives bankruptcy. Fraud,
          willful injury, and child support judgments are largely non-dischargeable.
        </p>
      </div>
    </div>
  );
};

const DocumentsSub = ({ debtor, patch }) => (
  <div>
    <SubSectionHeader>Documents</SubSectionHeader>
    <DropUpload
      documents={debtor.documents || []}
      onChange={(docs) => patch({ documents: docs })}
      testid="debtor-documents"
    />
  </div>
);

export const DEBTOR_SUBSTEPS = [
  { key: "basic", label: "Basic Info", Component: BasicInfo },
  { key: "address", label: "Address History", Component: AddressHistory },
  { key: "employment", label: "Employment", Component: Employment },
  { key: "assets", label: "Assets", Component: Assets },
  { key: "judgment", label: "Judgment Details", Component: JudgmentDetails },
  { key: "documents", label: "Documents", Component: DocumentsSub },
];

/**
 * Standalone debtor form used by:
 *   - Onboarding step 3 (Standard) — sub-stepped
 *   - Add Debtor modal (Standard) — sub-stepped
 *   - Add Debtor page (PRO) — stacked (advanced mode)
 *
 * Renders a single sub-step when `subIndex` is defined, else renders all sections stacked.
 */
export const DebtorForm = ({
  debtor,
  patch,
  subIndex = null,
}) => {
  if (subIndex == null) {
    // Stacked (PRO advanced mode)
    return (
      <div className="space-y-8">
        {DEBTOR_SUBSTEPS.map(({ key, Component }) => (
          <Component key={key} debtor={debtor} patch={patch} />
        ))}
      </div>
    );
  }
  const { Component } = DEBTOR_SUBSTEPS[subIndex] || DEBTOR_SUBSTEPS[0];
  return <Component debtor={debtor} patch={patch} />;
};

/**
 * Small pill row that renders the sub-step progress inside a Debtor wizard card.
 */
export const SubStepPills = ({ index, onSelect }) => (
  <div className="flex flex-wrap gap-1 mb-6" data-testid="debtor-substep-pills">
    {DEBTOR_SUBSTEPS.map((s, i) => (
      <button
        key={s.key}
        type="button"
        onClick={() => onSelect && onSelect(i)}
        className={cn(
          "h-8 px-3 rounded-full text-[11px] tracking-[0.06em] uppercase font-semibold transition-all",
          i === index
            ? "bg-accent-pink text-[#091424]"
            : i < index
            ? "bg-bg-tertiary text-text-secondary hover:text-white"
            : "bg-bg-primary text-text-muted border border-border-default"
        )}
        data-testid={`debtor-substep-pill-${i}`}
      >
        {s.label}
      </button>
    ))}
  </div>
);
